import { isErrorCode, type ErrorCode } from './protocol.ts'
import { validateTheme } from './theme.ts'
import type { CardDraft, PaymentMethodSummary, SessionConfig } from './types.ts'
import {
  integerOrNull,
  isRecord,
  isStringArray,
  paymentMethodPkid
} from './guards.ts'

export const TERMINAL_PATHS = {
  exchange: '/v1/terminal/sessions/exchange',
  paymentMethods: '/v1/terminal/payment-methods'
} as const

const MESSAGES: Record<ErrorCode, string> = {
  session_expired: 'This session has ended. Open the terminal again.',
  origin_rejected: 'This page is not allowed to open the terminal.',
  action_forbidden: 'This action is not allowed for this session.',
  customer_not_found: 'The customer was not found.',
  customer_ambiguous: 'More than one customer matches. Contact support.',
  duplicate_card: 'This card is already saved for this customer.',
  validation: 'Some card details are not valid. Check them and try again.',
  processor_decline: 'The card was declined. Try another card.',
  rate_limited: 'Too many attempts. Wait a moment and try again.',
  version_unsupported: 'This terminal version is not supported.',
  unavailable: 'The terminal is not available. Try again later.'
}

const STATUS_CODES: Record<number, ErrorCode> = {
  400: 'validation',
  401: 'session_expired',
  402: 'processor_decline',
  403: 'action_forbidden',
  410: 'session_expired',
  422: 'validation',
  429: 'rate_limited'
}

/** An API failure carrying a protocol error code and user-facing copy. */
export class TerminalApiError extends Error {
  readonly code: ErrorCode

  constructor(code: ErrorCode) {
    super(MESSAGES[code])
    this.name = 'TerminalApiError'
    this.code = code
  }
}

export interface TerminalApiConfig {
  baseUrl: string
  timeoutMs?: number
  fetch?: typeof fetch
}

export interface ExchangeInput {
  clientSecret: string
  parentOrigin: string
  terminalId: string
}

export interface TerminalApi {
  exchange: (
    input: ExchangeInput
  ) => Promise<{ idToken: string; session: SessionConfig }>
  listMethods: (idToken: string) => Promise<PaymentMethodSummary[]>
  createCardMethod: (
    idToken: string,
    draft: CardDraft,
    idempotencyKey: string
  ) => Promise<PaymentMethodSummary>
}

/**
 * Returns the user-facing copy for an error code.
 * @param code - The protocol error code.
 * @returns The message.
 */
export function messageFor(code: ErrorCode): string {
  return MESSAGES[code]
}

function toMethod(value: unknown): PaymentMethodSummary | null {
  if (!isRecord(value)) return null
  const { paymentType, brand, last4 } = value
  const paymentMethodId = paymentMethodPkid(value.paymentMethodId)
  const expMonth = integerOrNull(value.expMonth)
  const expYear = integerOrNull(value.expYear)
  if (paymentMethodId === null) return null
  if (paymentType !== 'card' && paymentType !== 'ach') return null
  if (brand !== null && typeof brand !== 'string') return null
  if (typeof last4 !== 'string' || !/^\d{4}$/.test(last4)) return null
  if (expMonth === undefined || expYear === undefined) return null
  return { paymentMethodId, paymentType, brand, last4, expMonth, expYear }
}

function toSession(body: Record<string, unknown>): SessionConfig | null {
  const { actions, actionId, sessionExpiresAt, locale } = body
  if (!isStringArray(actions)) {
    return null
  }
  if (typeof actionId !== 'string' || typeof sessionExpiresAt !== 'string') {
    return null
  }
  const theme = validateTheme(body.theme ?? {})
  return {
    actions,
    actionId,
    sessionExpiresAt,
    theme: theme.ok ? theme.theme : {},
    locale: typeof locale === 'string' ? locale : 'en-US'
  }
}

async function errorFrom(response: Response): Promise<TerminalApiError> {
  const body: unknown = await response.json().catch(() => null)
  const code = isRecord(body) ? body.code : undefined
  if (isErrorCode(code)) return new TerminalApiError(code)
  return new TerminalApiError(STATUS_CODES[response.status] ?? 'unavailable')
}

/**
 * Creates the client for the terminal session and payment-method routes.
 * @param config - The API origin and an optional fetch implementation.
 * @returns The client.
 */
export function createTerminalApi(config: TerminalApiConfig): TerminalApi {
  const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis)
  const timeoutMs = config.timeoutMs ?? 30_000

  async function call(
    path: string,
    init: { method: string; headers?: Record<string, string>; body?: unknown }
  ): Promise<Record<string, unknown>> {
    let response: Response
    try {
      response = await fetchImpl(`${config.baseUrl}${path}`, {
        method: init.method,
        headers: {
          accept: 'application/json',
          ...(init.body === undefined
            ? {}
            : { 'content-type': 'application/json' }),
          ...init.headers
        },
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        signal: AbortSignal.timeout(timeoutMs)
      })
    } catch {
      throw new TerminalApiError('unavailable')
    }
    if (!response.ok) throw await errorFrom(response)
    const body: unknown = await response.json().catch(() => null)
    if (!isRecord(body)) throw new TerminalApiError('unavailable')
    return body
  }

  return {
    async exchange(input) {
      const body = await call(TERMINAL_PATHS.exchange, {
        method: 'POST',
        body: input
      })
      const session = toSession(body)
      if (typeof body.idToken !== 'string' || session === null) {
        throw new TerminalApiError('unavailable')
      }
      return { idToken: body.idToken, session }
    },
    async listMethods(idToken) {
      const body = await call(TERMINAL_PATHS.paymentMethods, {
        method: 'GET',
        headers: { authorization: `Bearer ${idToken}` }
      })
      const list = Array.isArray(body.paymentMethods) ? body.paymentMethods : []
      return list
        .map(toMethod)
        .filter((m): m is PaymentMethodSummary => m !== null)
    },
    async createCardMethod(idToken, draft, idempotencyKey) {
      const body = await call(TERMINAL_PATHS.paymentMethods, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
          'idempotency-key': idempotencyKey
        },
        body: draft
      })
      const method = toMethod(body)
      if (method === null) throw new TerminalApiError('unavailable')
      return method
    }
  }
}
